import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { statusCssClass, statusLabel } from '../quotation.utils';

@Component({
    selector: 'app-quotation-status-badge',
    standalone: true,
    imports: [CommonModule],
    template: `<span class="status-pill" [ngClass]="cssClass">{{ label }}</span>`
})
export class QuotationStatusBadgeComponent {
    @Input() status = '';

    get label(): string {
        return statusLabel(this.status);
    }

    get cssClass(): string {
        return statusCssClass(this.status);
    }
}


@Component({
    selector: 'app-quotation-kpi-bar',
    standalone: true,
    imports: [CommonModule],
    template: `
        <div class="kpi-bar">
            <button type="button" class="kpi-card" [class.active]="activeFilter === 'ALL'" (click)="filterSelect.emit('ALL')">
                <span class="kpi-label">Total</span>
                <span class="kpi-value">{{ stats.total }}</span>
            </button>
            <button type="button" class="kpi-card" [class.active]="activeFilter === 'SUBMITTED'" (click)="filterSelect.emit('SUBMITTED')">
                <span class="kpi-label">Submitted</span>
                <span class="kpi-value kpi-submitted">{{ stats.submitted }}</span>
            </button>
            <button type="button" class="kpi-card" [class.active]="activeFilter === 'UNDER_REVIEW'" (click)="filterSelect.emit('UNDER_REVIEW')">
                <span class="kpi-label">Under review</span>
                <span class="kpi-value kpi-review">{{ stats.underReview }}</span>
            </button>
            <button type="button" class="kpi-card" [class.active]="activeFilter === 'PUNCHED'" (click)="filterSelect.emit('PUNCHED')">
                <span class="kpi-label">Punched</span>
                <span class="kpi-value kpi-approved">{{ stats.punched }}</span>
            </button>
            <button type="button" class="kpi-card" [class.active]="activeFilter === 'REJECTED'" (click)="filterSelect.emit('REJECTED')">
                <span class="kpi-label">Rejected</span>
                <span class="kpi-value kpi-rejected">{{ stats.rejected }}</span>
            </button>
        </div>
    `,
    styles: [
        `
            .kpi-bar {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(8.5rem, 1fr));
                gap: 0.75rem;
            }
            .kpi-card {
                text-align: left;
                background: var(--dispute-card);
                border: 1px solid var(--dispute-border);
                border-radius: 12px;
                padding: 0.85rem 1rem;
                cursor: pointer;
            }
            .kpi-card.active {
                border-color: var(--dispute-primary);
                box-shadow: 0 0 0 1px var(--dispute-primary);
            }
            .kpi-label {
                display: block;
                font-size: 0.75rem;
                color: var(--dispute-muted);
                text-transform: uppercase;
                letter-spacing: 0.04em;
            }
            .kpi-value {
                font-size: 1.4rem;
                font-weight: 700;
                color: var(--dispute-kpi-total);
            }
            .kpi-submitted { color: var(--dispute-kpi-submitted); }
            .kpi-review { color: var(--dispute-kpi-review); }
            .kpi-approved { color: var(--dispute-kpi-approved); }
            .kpi-rejected { color: var(--dispute-kpi-rejected); }
        `
    ]
})
export class QuotationKpiBarComponent {
    @Input({ required: true }) stats!: {
        total: number;
        submitted: number;
        underReview: number;
        punched: number;
        rejected: number;
    };
    @Input() activeFilter: string = 'ALL';
    @Output() filterSelect = new EventEmitter<string>();
}

@Component({
    selector: 'app-quotation-table',
    standalone: true,
    imports: [CommonModule, DatePipe, QuotationStatusBadgeComponent],
    template: `
        <div class="dispute-card" style="padding:0;overflow:hidden">
            <table class="q-table">
                <thead>
                    <tr>
                        <th>Draft order</th>
                        <th>Customer</th>
                        <th>Status</th>
                        <th>Lines</th>
                        <th>Created</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let row of quotations" (click)="view.emit(row.quotationNumber)">
                        <td class="strong">{{ row.quotationNumber }}</td>
                        <td>{{ row.customerName || row.customerId || '—' }}</td>
                        <td><app-quotation-status-badge [status]="row.status"></app-quotation-status-badge></td>
                        <td>{{ row.lineCount || 0 }}</td>
                        <td class="muted">{{ row.createdAt | date: 'MMM d, y, h:mm a' }}</td>
                        <td class="actions">
                            <button type="button" class="view-btn" (click)="view.emit(row.quotationNumber); $event.stopPropagation()">View</button>
                        </td>
                    </tr>
                    <tr *ngIf="!quotations?.length">
                        <td colspan="6" class="empty">No draft orders found for the selected filters.</td>
                    </tr>
                </tbody>
            </table>
        </div>
    `,
    styles: [
        `
            .q-table {
                width: 100%;
                border-collapse: collapse;
            }
            .q-table th,
            .q-table td {
                padding: 0.75rem 1rem;
                border-bottom: 1px solid var(--dispute-border);
                text-align: left;
                font-size: 0.9rem;
            }
            .q-table th {
                font-size: 0.72rem;
                text-transform: uppercase;
                letter-spacing: 0.04em;
                color: var(--dispute-muted);
            }
            .q-table tbody tr {
                cursor: pointer;
            }
            .q-table tbody tr:hover {
                background: var(--dispute-row-hover);
            }
            .strong { font-weight: 700; }
            .muted { color: var(--dispute-muted); white-space: nowrap; font-size: 0.85rem; }
            .empty { text-align: center; padding: 2rem !important; color: var(--dispute-muted); }
            .actions { text-align: right; }
            .view-btn {
                border: 1px solid var(--dispute-border);
                background: var(--dispute-card);
                border-radius: 6px;
                padding: 0.35rem 0.75rem;
                cursor: pointer;
                font-size: 0.8rem;
            }
        `
    ]
})
export class QuotationTableComponent {
    @Input({ required: true }) quotations: any[] = [];
    @Output() view = new EventEmitter<string>();
}
